import { useState } from 'react';
import styled from 'styled-components';
import { Collapse } from '@mui/material';
import { pluralizeString } from '../../../../utils/common';
import Button from '../../../Button';
import { Flex, FlexColumn } from '../../../Flex';
import type { Column } from '../../../Table/types';
import ChatCodeBlock from '../../markdownRenderers/ChatCodeBlock';
import type { ApprovalToolRendererProps } from '../../components/ApprovalToolRenderer';
import {
  CollapseArrowIcon,
  formatToolName,
} from '../../components/toolDisplay';
import {
  ApprovalCard,
  ApprovalCardBody,
  ApprovalCardHeaderButton,
  ApprovalCardTitle,
} from '../approvalCard';
import { neverReachedApproval } from '../planApprovalStatus';
import { ChatTable } from '../tableToolRenderer';
import {
  displayWarehouseQuerySql,
  isWarehouseQueryRejected,
  readWarehouseQuerySample,
} from './queryResult';

const Meta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray14};
`;

const Note = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray12};
`;

const ErrorText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.error.main};
  white-space: pre-wrap;
  word-break: break-word;
`;

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const sampleSummary = (
  returnedRows: number,
  rowCount: number,
  truncated: boolean,
): string => {
  const returned = pluralizeString(returnedRows, 'row', 'rows');
  if (!truncated) return returned;
  return `Showing ${returned} of ${rowCount}`;
};

const RunWarehouseQueryToolRenderer = ({
  invocation,
  onApproveToolUse,
  isActive,
  interactionsDisabled,
}: ApprovalToolRendererProps) => {
  const [userResponded, setUserResponded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const sql = displayWarehouseQuerySql(invocation.args);
  const sample = readWarehouseQuerySample(invocation.result?.result.result);
  const rejected = isWarehouseQueryRejected(invocation);
  const failed = invocation.result?.isError === true && !rejected;
  const status = rejected ? 'rejected' : sample ? 'confirmed' : undefined;
  const showActions = isActive && !userResponded && !invocation.result;
  const rowLimit = invocation.args?.row_limit;

  const columns: Column<Record<string, unknown>>[] =
    sample?.columns.map((name) => ({
      id: name,
      header: name,
      filterable: false,
      renderer: (row) => <>{formatCell(row[name])}</>,
    })) ?? [];

  const respond = (approved: boolean) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    void onApproveToolUse(approved).then(
      (result) => {
        if (result === 'delivered') setUserResponded(true);
        else setIsSubmitting(false);
      },
      () => setIsSubmitting(false),
    );
  };

  return (
    <ApprovalCard $status={status}>
      <ApprovalCardHeaderButton
        $status={status}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <Flex sx={{ alignItems: 'center', gap: '8px' }}>
          <CollapseArrowIcon $expanded={isExpanded} $dimWhenCollapsed={false} />
          <ApprovalCardTitle $status={status}>
            {formatToolName(invocation.toolName)}
          </ApprovalCardTitle>
        </Flex>
        {typeof rowLimit === 'number' && (
          <Meta>Sample limit: {pluralizeString(rowLimit, 'row', 'rows')}</Meta>
        )}
      </ApprovalCardHeaderButton>
      <Collapse in={isExpanded}>
        <ApprovalCardBody>
          <ChatCodeBlock value={sql} language="sql" />
          {sample && (
            <FlexColumn sx={{ gap: '8px' }}>
              <Meta>
                {sampleSummary(
                  sample.returnedRows,
                  sample.rowCount,
                  sample.truncated,
                )}
              </Meta>
              <ChatTable rows={sample.rows} columns={columns} />
            </FlexColumn>
          )}
          {failed && (
            <ErrorText>
              {invocation.result?.error || 'The query failed.'}
            </ErrorText>
          )}
          {rejected && <Note>You rejected this query.</Note>}
          {showActions && (
            <Flex sx={{ justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                variant="outlined"
                size="extraSmall"
                disabled={interactionsDisabled || isSubmitting}
                onClick={() => respond(false)}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="extraSmall"
                disabled={interactionsDisabled || isSubmitting}
                onClick={() => respond(true)}
              >
                Approve
              </Button>
            </Flex>
          )}
          {isActive && userResponded && <Meta>Running the query...</Meta>}
        </ApprovalCardBody>
      </Collapse>
    </ApprovalCard>
  );
};

const runWarehouseQueryApprovalRenderer = ({
  invocation,
  onApproveToolUse,
  isActive,
  interactionsDisabled,
}: ApprovalToolRendererProps) => {
  if (neverReachedApproval(invocation)) {
    return null;
  }
  return (
    <RunWarehouseQueryToolRenderer
      invocation={invocation}
      onApproveToolUse={onApproveToolUse}
      isActive={isActive}
      interactionsDisabled={interactionsDisabled}
    />
  );
};

export default runWarehouseQueryApprovalRenderer;
